import { AxiosInstance } from "axios"
import {
    Acquirer,
    Agreement,
    WellknownAgreementType,
    IndustryCode,
    BankAccount,
    CreateIndustryCodeBody,
} from "./SupplierAdminPOSTypes"

export enum POSQuery {
    AGREEMENTS_DETAILS = "agreements-for-supplier",
    ACQUIRERS_DETAILS = "acquirers-for-supplier",
    INDUSTRY_CODES_DETAILS = "industry-codes-for-supplier",
    BANK_ACCOUNT_CODES_DETAILS = "bank-accounts-for-supplier",
}

// ACQUIRERS
type FetchAcquirersParams = Readonly<{
    supplierId: number
}>

export type FetchAcquirersResponse = Readonly<{
    acquirers: Acquirer[]
}>

export async function fetchAcquirersForSupplier(
    { supplierId }: FetchAcquirersParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchAcquirersResponse> {
    const url = `/suppliers/${supplierId}/acquirers`

    const { data } = await axios.get(url, {
        params: {},
        signal,
    })

    return data
}

// Acquirer agreements

type FetchAcquirerAgreementsParams = Readonly<{
    supplierId: number
}>

export type FetchAcquirerAgreementsResponse = Readonly<{
    agreements: Agreement[]
}>

export async function fetchAcquirerAgreementsForSupplier(
    { supplierId }: FetchAcquirerAgreementsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchAcquirerAgreementsResponse> {
    const url = `/suppliers/${supplierId}/acquirers/agreements`

    const { data } = await axios.get(url, {
        params: {
            type: WellknownAgreementType.ADYEN_POS,
        },
        signal,
    })

    return data
}

// Accept terms and conditions

type acceptTermsAndConditionsParams = Readonly<{
    supplierId: number
    agreementId: number
}>

type AcceptTermsAndConditionsResponse = Readonly<{}>

export async function acceptTermsAndConditions(
    { supplierId, agreementId }: acceptTermsAndConditionsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<AcceptTermsAndConditionsResponse> {
    const url = `/acquirers/agreements/acceptTermsAndConditions`

    const { data } = await axios.post(
        url,
        {
            termsAndConditions: {
                supplier: {
                    id: supplierId,
                },
                agreement: {
                    id: agreementId,
                },
            },
        },
        {
            signal,
        },
    )

    return data
}

// Create Acquirer agreement

type createAcquirerAgreementParams = Readonly<{
    agreement: {
        supplier: { id: number }
        type: WellknownAgreementType.ADYEN_POS
        acquirer: {
            id: number
        }
    }
}>

type AgreementResponse = Readonly<{
    agreement: Agreement
}>

export async function createAcquirerAgreement(
    { agreement }: createAcquirerAgreementParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<AgreementResponse> {
    const url = `/acquirers/agreements`

    const { data } = await axios.post(
        url,
        {
            agreement,
        },
        {
            signal,
        },
    )

    return data
}

type ProceedWithOnboardingParams = Readonly<{
    agreementId: number
}>

export async function proceedWithOnboarding(
    { agreementId }: ProceedWithOnboardingParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<AgreementResponse> {
    const url = `acquirers/agreements/${agreementId}`

    const { data } = await axios.post(url, undefined, {
        signal,
    })

    return data
}

// Bank accounts + Industry codes

type FetchIndustryCodesResultParams = Readonly<{
    supplierId: number
}>

export type FetchIndustryCodesResultResponse = Readonly<{
    industryCodes: IndustryCode[]
}>

export async function fetchIndustryCodesResult(
    { supplierId }: FetchIndustryCodesResultParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchIndustryCodesResultResponse> {
    const url = `/suppliers/${supplierId}/acquirers/industryCodes`

    const { data } = await axios.get(url, {
        params: {},
        signal,
    })

    return data
}

type FetchBankAccountsParams = Readonly<{
    supplierId: number
}>

export type FetchBankAccountsResponse = Readonly<{
    bankAccounts: BankAccount[]
}>

export async function fetchBankAccountsResult(
    { supplierId }: FetchBankAccountsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchBankAccountsResponse> {
    const url = `/suppliers/${supplierId}/acquirers/bankAccounts`

    const { data } = await axios.get(url, {
        params: {},
        signal,
    })

    return data
}

type ServiceResponse = Readonly<{
    serviceResponse: {
        displayMessage: string
    }
}>

type CreateIndustryCodeParams = Readonly<{
    agreementId: number
    industryCode: CreateIndustryCodeBody
}>

type CreateIndustryCodeResponse = ServiceResponse

export async function createIndustryCode(
    { agreementId, industryCode }: CreateIndustryCodeParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<CreateIndustryCodeResponse> {
    const url = `/acquirers/agreements/${agreementId}/industryCodes`

    const { data } = await axios.post(
        url,
        {
            industryCode,
        },
        {
            signal,
        },
    )

    return data
}

type DeleteIndustryCodeItemResponse = ServiceResponse

type DeleteIndustryCodeItemParam = Readonly<{
    supplierId: number
    industryCodeId: number | string
}>

export async function deleteIndustryCodeItem(
    { supplierId, industryCodeId }: DeleteIndustryCodeItemParam,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<DeleteIndustryCodeItemResponse> {
    const url = `/acquirers/agreements/${supplierId}/industryCodes/${industryCodeId}`
    const { data } = await axios.delete(url, { signal })
    return data
}

type DeleteBankAccountItemResponse = ServiceResponse

type DeleteBankAccountItemParam = Readonly<{
    supplierId: number
    bankAccountId: number | string
}>

export async function deleteBankAccountItem(
    { supplierId, bankAccountId }: DeleteBankAccountItemParam,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<DeleteBankAccountItemResponse> {
    const url = `/acquirers/agreements/${supplierId}/bankAccounts/${bankAccountId}`
    const { data } = await axios.delete(url, { signal })
    return data
}
